import { apiClient } from "./apiClient";
import type {
  TicketCategory,
  TicketDetail,
  TicketListItem,
  TicketMessage,
  TicketStatus,
  UnreadCount,
} from "../../types/ticket";

export interface TicketCreatePayload {
  subject: string;
  /** Opcional cuando se pasa `planning_group`: el backend autoasigna
   *  la categoría con kind='planning' si viene planning_group y
   *  category está vacío. */
  category?: number;
  body_html: string;
  attachments?: File[];
  /** Cuando se pasa, crea un hilo de comentarios sobre la
   *  PlanningGroup indicada. */
  planning_group?: number;
  /** Cuando se pasa, referencia un productor específico — usado por
   *  la categoría "Solicitud de auditoría de productor" del perfil
   *  Eurosemillas. */
  producer?: number;
}

export interface TicketReplyPayload {
  body_html: string;
  attachments?: File[];
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
}

const buildMultipart = (
  body: Record<string, any>,
  files: File[] = [],
): FormData => {
  const fd = new FormData();
  Object.entries(body).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    fd.append(k, String(v));
  });
  files.forEach((f) => fd.append("attachments", f, f.name));
  return fd;
};

export const ticketService = {
  async listCategories(): Promise<TicketCategory[]> {
    const r = await apiClient.get<TicketCategory[]>("/v1/ticket-categories/");
    return r.data;
  },

  async listTickets(
    params: {
      page?: number;
      pageSize?: number;
      ordering?: string;
      /** Acota el listado a los hilos de comentarios de una planning
       *  concreta (usado en el panel "Comentarios" de PlanningGroupShow). */
      planning_group?: number;
      /** Filtro por category kind. `planning` acota a los hilos de
       *  comentarios de planning; `audit` a incidencias, etc. */
      "category__kind"?: string;
    } = {},
  ): Promise<PaginatedResponse<TicketListItem>> {
    const r = await apiClient.get("/v1/tickets/", { params });
    return r.data;
  },

  async getTicket(id: number): Promise<TicketDetail> {
    const r = await apiClient.get<TicketDetail>(`/v1/tickets/${id}/`);
    return r.data;
  },

  async createTicket(payload: TicketCreatePayload): Promise<TicketDetail> {
    const fd = buildMultipart(
      {
        subject: payload.subject,
        category: payload.category,
        body_html: payload.body_html,
        planning_group: payload.planning_group,
        producer: payload.producer,
      },
      payload.attachments ?? [],
    );
    const r = await apiClient.post<TicketDetail>("/v1/tickets/", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  },

  async addMessage(
    ticketId: number,
    payload: TicketReplyPayload,
  ): Promise<TicketMessage> {
    const fd = buildMultipart(
      { body_html: payload.body_html },
      payload.attachments ?? [],
    );
    const r = await apiClient.post<TicketMessage>(
      `/v1/tickets/${ticketId}/messages/`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return r.data;
  },

  async markRead(ticketId: number): Promise<void> {
    await apiClient.post(`/v1/tickets/${ticketId}/mark-read/`);
  },

  async unreadCount(): Promise<UnreadCount> {
    const r = await apiClient.get<UnreadCount>("/v1/tickets/unread-count/");
    return r.data;
  },

  async updateStatus(
    ticketId: number,
    status: TicketStatus,
    resolution?: {
      resolution_notes?: string;
      regularized_surface?: number | null;
    },
  ): Promise<TicketDetail> {
    const payload: Record<string, unknown> = { status };
    if (resolution?.resolution_notes !== undefined) {
      payload.resolution_notes = resolution.resolution_notes;
    }
    if (resolution?.regularized_surface !== undefined) {
      payload.regularized_surface = resolution.regularized_surface;
    }
    const r = await apiClient.patch<TicketDetail>(
      `/v1/tickets/${ticketId}/`,
      payload,
    );
    return r.data;
  },
};

export default ticketService;
